// hook dependancies
import { useState, useEffect, useGlobal, setGlobal } from "reactn";

/**
 * hook to get countries from the API
 * this is used to populate the drop down list of destinations for step one
 */
const AppHook = (validate, callback) => {
  /**
   * `values` stores an object of form values. It gets populated on submit and keyup.
   * `setValues` is a function used to populate the `values` object.
   * `values` is used for Form validation and passing values to the api.
   */
  const [values] = useGlobal("values");

  /**
   * `errors` stores form error messages.
   * `setErrors` is used with `validate` to populated the errors object e.g `setErrors(validate(values));`
   */
  const [errors, setErrors] = useState({});

  /**
   * `focus` used with onblur and obfocus to store the currently focued form values
   * `focus` is returened and imported in RegistrationForm to check and
   * set if focus is true, we used this to handle label animations.
   */
  const [focus, setFocus] = useState({});

  /**
   * `isSubmitting` set to false by default. When form is submitting this is set to `true`
   * it is used to detect if the submit button has been pressed. OnkeyUp this is set to
   * false again so the form doesnt submit on keyup.
   */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * `isValidating` set to true on blu so we can validate on the fly without doing an api call by
   * setting isSubmitting to true.
   */
  const [isValidating, setIsValidating] = useState(false);

  /**
   * `isLoading` is set to false by default. On submit this is set to `true` and then set to `false`
   * again when the api has a response. Used on the submit button to disable the button if `true` and
   * enable it again if `false`.
   */
  const [isLoading, setIsLoading] = useState(false);

  /**
   * useEffect replaces the `componentDidMount` and `componentDidUpdate` lifecycle methods in React Class components.
   * This is were we handle the form submission. We pass [errors] as the second parameter for useEffect so we can run
   * it every time `[errors] changes.
   */
  useEffect(() => {
    // check if there are no errors and the submit button has been pressed
    if (Object.keys(errors).length === 0 && isSubmitting) {
      if (callback) callback();
      // do a post to the registration api and pass the form values
    }
  }, [errors, isSubmitting, callback, values]);

  /**
   * Handles form submit
   */
  const handleSubmit = async event => {
    if (event) event.preventDefault();
    // validates form fields ]and sets errors.

    setErrors(validate(values));
    setIsSubmitting(true);
    setIsValidating(true);
  };

  /**
   * Handles input change events
   */
  const handleChange = event => {
    event.persist();
    // sets `values` object which is used for validation and form submission
    setGlobal({
      values: { ...values, [event.target.name]: event.target.value }
    });
  };

  /**
   * Handles input focus
   */
  const handleFocus = event => {
    if (event) event.preventDefault();

    // sets focus to currently focused input
    setFocus(event.target.name);
  };

  /**
   * Handles input blur
   */
  const handleBlur = event => {
    if (event) event.preventDefault();

    // sets focus to null so label animation resets
    setFocus(null);
    if (!isValidating) {
      setErrors(validate(values, event));
    }

  };

  /**
   * Handles input keyups
   */
  const handleKeyup = () => {
    // set isSubmitting to false so the we dont do an api
    setIsSubmitting(false);

    // validates inputs on the fly
    if (isValidating) {
      setErrors(validate(values));
    }
  };

  // returning all varaibles and functions that we can to use inside other components
  return {
    handleChange,
    handleKeyup,
    handleSubmit,
    handleFocus,
    handleBlur,
    setIsSubmitting,
    setIsLoading,
    setErrors,
    isLoading,
    values,
    errors,
    focus,
    isSubmitting
  };
};

export default AppHook;
